import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import { useParams } from 'react-router';
import { AgGridReact } from 'ag-grid-react';
import AppContext from 'AppContext';
import GrpSpinner from 'Components/GrpSpinner/GrpSpinner';
import { getTargetProfilePreview, getTargetProfileSelectedReports } from 'Services/distributionApi';
import classes from './TargetProfilePreview.module.scss';

const getReportStatus = (report) => {
  if (!report) return 'NOREPORT';
  if (!report.IsReportSetup || !report.IsLanguageAvailable) return 'NA';
  if (report.ReportCount < 1 || report.ReportApprovedCount < report.ReportCount) return 'NOTREADY';
  return 'READY';
};

const ReportStatusBadge = ({ report }) => {
  const status = getReportStatus(report);

  const statusConfig = {
    READY: { label: 'Ready', className: classes.badgeSuccess, icon: '✓', tooltipCaption: "Report is ready for distribution" },
    NOTREADY: { label: 'Not Ready', className: classes.badgeDanger, icon: '✕', tooltipCaption: "Report is not ready for distribution" },
    NA: { label: 'N/A', className: classes.badgeWarning, icon: '?',  tooltipCaption:"Please contact your production team"},
    NOREPORT: { label: '', className: classes.badgeMuted, icon: '' , tooltipCaption: "No report is being requested for that share class and that language in the target profile"},
  };

  const config = statusConfig[status];

  return (
    <div className={classes.badgeContainer}>
      {config.icon && (
        <span className={`${classes.badge} ${config.className}`} title={config.tooltipCaption} style={{width:"auto",height:"auto",padding:"0.5em"}}>
          {config.label}
          &nbsp;
          {config.icon}
        </span>
      )}
    </div>
  );
};

const TargetProfilePreview = () => {
  const params = useParams();
  const TargetProfileId = params.TargetProfileId || params.id;
  const context = useContext(AppContext);
  const gridRef = useRef(null);

  const [rowData, setRowData] = useState([]);
  const [selectedReports, setSelectedReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasReportingPeriod, setHasReportingPeriod] = useState(true);
  const profileId = Number.parseInt(TargetProfileId, 10);
  const reportingDate = context.state.selectedReportingDate;

  useEffect(() => {
    const loadData = async () => {
      if (!reportingDate?.code) {
        setHasReportingPeriod(false);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const [previewResult, reportsResult] = await Promise.all([
          getTargetProfilePreview(profileId, reportingDate.code),
          getTargetProfileSelectedReports(profileId),
        ]);

        const previewData = previewResult?.Content || previewResult?.value || previewResult || [];
        const reportsData = reportsResult?.Content || reportsResult?.value || reportsResult || [];

        setRowData(Array.isArray(previewData) ? previewData : []);
        setSelectedReports(Array.isArray(reportsData) ? reportsData : []);
      } catch (err) {
        context.handlers.setToast({ type: 'error', body: <>{err.human || 'Failed to load preview'}</> });
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [profileId, reportingDate, context.handlers]);

  const baseColumns = [
    { headerName: 'Fund Code', field: 'FundCode', width: 180, filter: 'agTextColumnFilter' },
    { headerName: 'Share Class Code', field: 'ClassCode', width: 180, filter: 'agTextColumnFilter' },
    { headerName: 'Share Class Name', field: 'ClassName', flex: 1, filter: 'agTextColumnFilter' },
    {
      headerName: 'Language',
      width: 180,
      filter: 'agTextColumnFilter',
      valueGetter: (params) => `${params.data.LanguageName} (${params.data.IsoLanguage})`,
    },
  ];

  // Dynamic columns for each selected report type
  const reportColumns = useMemo(() => selectedReports.map((targetReport) => ({
    headerName: targetReport.ReportType?.TypeName || `Report ${targetReport.ReportTypeId}`,
    width: 100,
    sortable: false,
    filter: false,
    headerClass: classes.centeredHeader,
    cellRenderer: (params) => {
      const report = params.data.Reports?.find(
        (r) => r.ReportTypeId === targetReport.ReportTypeId,
      );
      return <ReportStatusBadge report={report} />;
    },
  })), [selectedReports]);

  const columnDefs = [...baseColumns, ...reportColumns];

  if (loading) {
    return <GrpSpinner />;
  }

  if (!hasReportingPeriod) {
    return (
      <div className={classes.container}>
        <h3>Target Profile Preview</h3>
        <p>
          Please select a reporting period to show the profile preview for that period.
        </p>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <h3>Target Profile Preview</h3>
      <div className={`ag-theme-reborn ${classes.gridWrapper}`}>
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={{
            sortable: true,
            resizable: true,
            filter: true,
          }}
          pagination={true}
          paginationPageSize={50}
          domLayout="autoHeight"
        />
      </div>
    </div>
  );
};

export default TargetProfilePreview;
